import { Subject } from "rxjs"
import { finalize } from "rxjs/operators"
import { Emitter as EventEmitter } from "strict-event-emitter"

import { ConnectionStatus, RpcException, RpcTimeoutException } from "@/lib/client/types.ts"
import { WEBSOCKET_REQUEST_DELAY_MS, WebSocketStableConnection } from "@/lib/client/WebSocketStableConnection.ts"
import { getLogger } from "@/lib/logging.ts"
import { maybeSetTimeout } from "@/lib/std.ts"

import type { RequestPacket, ResponsePacket, RpcResult } from "@/lib/client/types.ts"
import type { TokenSource } from "@/lib/client/WebSocketStableConnection.ts"

const DEFAULT_TIMEOUT = 60000

const log = getLogger("ApiService")

interface PendingRequest<T> {
  resolve: (value: RpcResult<T, unknown>) => void
  reject: (error: Error) => void
  timer: NodeJS.Timeout
  packet: RequestPacket
  path: string
}

interface ActiveFlux<T> {
  stream: Subject<T>
  requestId: number
  path: string
}

interface FluxRequest {
  id: string
  path: string
  payload: Record<string, unknown>
  timeout: number
}

type Events = {
  status: [ConnectionStatus]
}

export class ApiService extends EventEmitter<Events> {
  private nextId: number = 1
  private connection: WebSocketStableConnection

  // All pending requests
  private pendingRequests = new Map<number, PendingRequest<unknown>>()

  // Connection queue determines the order in which pending requests are sent
  private connectionQueue = new Set<number>()

  // Flux becomes active when we receive the response from the server
  private activeFluxes = new Map<string, ActiveFlux<unknown>>()

  // All flux requests. We need to store them to be able to resubscribe to fluxes when the connection is lost and then restored.
  private fluxRequests: FluxRequest[] = []

  // We need to reuse the same stream that old flux had been using. So we need to map new fluxId to the old one
  private fluxIdBeforeRestart = new Map<string, string>()

  constructor(baseUrl: string, tokenSource: TokenSource) {
    super()
    this.connection = new WebSocketStableConnection(baseUrl, tokenSource)
    tokenSource.onTokenChanged(/* token */_ => {
      // Do nothing. Previously we sent refreshed token to the server, but now server refreshes it itself
    })

    this.connection.on("message", (packet: ResponsePacket) => {
        this.handleResponse(packet)
      }
    )

    this.connection.on("status", (status: ConnectionStatus) => {
      this.emit("status", status)
      if (status === ConnectionStatus.Connected) {
        // Send all waiting requests
        maybeSetTimeout(() => {
          for (const id of this.connectionQueue) {
            const request = this.pendingRequests.get(id)
            if (request) {
              this.connection.send(request.packet)
            }
          }

          this.connectionQueue.clear()

          // If this.fluxRequests is not empty at this point, it contains fluxes that should be restarted
          const toRestart = this.fluxRequests.splice(0, this.fluxRequests.length)
          for (const fluxRequest of toRestart) {
            void this.rpc(fluxRequest.path, fluxRequest.payload, fluxRequest.timeout, fluxRequest.id)
          }
        }, WEBSOCKET_REQUEST_DELAY_MS)
      }
    })
  }

  private async terminateFluxes(fluxIds: string[]) {
    if (fluxIds.length === 0) return

    const id = this.nextId++
    const payload = { fluxIds }
    const packet: RequestPacket = {
      id,
      path: "FluxService/terminateFluxes",
      payload,
    }

    if (this.connection.status === ConnectionStatus.Connected) {
      maybeSetTimeout(() => {
        this.connection.send(packet)
      }, WEBSOCKET_REQUEST_DELAY_MS)
    }

    // Remove from active fluxes
    fluxIds.forEach(id => {
      this.activeFluxes.delete(id)
      const index = this.fluxRequests.findIndex(it => it.id == id)
      if (index > -1) {
        this.fluxRequests.splice(index, 1)
      }
    })
  }

  public rpc<R, U>(path: string, payload: Record<string, unknown> = {}, timeout: number = DEFAULT_TIMEOUT, fluxIdBeforeRestart: string | null = null): Promise<RpcResult<R, U>> {
    return new Promise<RpcResult<R, U>>((resolveTyped, reject) => {

      const id = this.nextId++

      // For flux requests, we need to prepare special meta
      let meta = undefined
      let fluxId = undefined

      if (path.startsWith("flux/")) {
        // Extract service and method from the flux path
        const [, service, method] = path.split("/")
        fluxId = `${service}::${method}/${id}`
        meta = { pushId: fluxId }

        if (fluxIdBeforeRestart) {
          this.fluxIdBeforeRestart.set(fluxId, fluxIdBeforeRestart)
        }

        this.fluxRequests.push({ id: fluxId, path, payload, timeout })
      }

      const packet: RequestPacket = {
        id,
        path,
        payload,
        meta,
      }

      const timer = setTimeout(() => {
        if (fluxId) {
          const flux = this.activeFluxes.get(fluxId)
          if (flux) {
            flux.stream.error(new RpcTimeoutException(`Request to ${path} has timed out after ${timeout}ms`))
            flux.stream.complete()
            void this.terminateFluxes([fluxId])
          }
        }
        this.connectionQueue.delete(id)
        this.pendingRequests.delete(id)
        reject(new RpcTimeoutException(`Request to ${path} has timed out after ${timeout}ms`))
      }, timeout)

      const resolve = resolveTyped as (value: RpcResult<R, unknown>) => void
      const pendingRequest: PendingRequest<R> = {
        resolve,
        reject,
        timer,
        packet,
        path
      }

      this.pendingRequests.set(id, pendingRequest as PendingRequest<unknown>)

      if (this.connection.status === ConnectionStatus.Connected) {
        maybeSetTimeout(() => {
          this.connection.send(packet)
        }, WEBSOCKET_REQUEST_DELAY_MS)
      } else {
        this.connectionQueue.add(id)
      }
    })
  }

  private handleResponse(packet: ResponsePacket) {
    // Handle regular RPC responses
    if (packet.id !== 0) {
      const request = this.pendingRequests.get(packet.id)
      if (!request) {
        // This is likely a response to a terminateFluxes request, no need to log a warning.
        // Those are sent straight away and not added to pendingRequests.
        if (packet.path !== "" || packet.payload !== "" || packet.failure !== null || packet.meta != null) {
          log.warn("Received response for unknown request:", packet)
        }
        return
      }

      clearTimeout(request.timer)
      this.pendingRequests.delete(packet.id)

      if (packet.failure) {
        request.reject(new RpcException(packet.failure, request.packet.path))
      } else {
        // For flux requests, this is the initial response
        if (request.packet.path.startsWith("flux/")) {
          const fluxId = request.packet.meta?.pushId as string

          const fluxIdBeforeRestart = this.fluxIdBeforeRestart.get(fluxId)
          if (fluxIdBeforeRestart) {
            this.fluxIdBeforeRestart.delete(fluxId)
          }
          const reusedStream = fluxIdBeforeRestart ? this.activeFluxes.get(fluxIdBeforeRestart)?.stream : null
          const stream = reusedStream && !reusedStream.closed ? reusedStream : new Subject<unknown>()
          const flux: ActiveFlux<unknown> = {
            stream,
            requestId: packet.id,
            path: request.packet.path
          }

          this.activeFluxes.set(fluxId, flux)

          const observable = stream.asObservable().pipe(
            finalize(() => {
              this.pendingRequests.delete(flux.requestId)
              void this.terminateFluxes([fluxId])
            })
          )
          request.resolve({
            type: "flux",
            initial: packet.payload,
            updates: observable
          })
        } else {
          request.resolve({
            type: "regular",
            value: packet.payload
          })
        }
      }
      return
    }

    // Handle flux responses (id === 0)
    // Extract fluxId from the path (remove 'flux/' prefix)
    const fluxId = packet.path.substring(5) // 'flux/'.length === 5
    const flux = this.activeFluxes.get(fluxId)
    if (!flux) {
      log.warn("Received response for unknown flux:", packet)
      return
    }

    if (!packet.failure) {
      flux.stream.next(packet.payload)
    } else {
      flux.stream.error(new RpcException(packet.failure, flux.path))
      flux.stream.complete()
      void this.terminateFluxes([fluxId])
      this.pendingRequests.delete(flux.requestId)
    }
  }
}